// Pure helpers for RFID tag management on the lift record. DOM-free so they unit-test
// directly. "RFID" is the customer-facing term; the internal route/token is "NFC" (NTAG213).

export type RfidTagStatus = "not_programmed" | "url_generated" | "sent" | "programmed";

// A tag lives on a lift or on a piece of care equipment (Session ACT). The kind decides the
// public URL shape and whether the engineer auto-dialler line is appended (lift-only).
export type RfidAssetKind = "lift" | "equipment";

// Session AMZ (the Guv POP Tag, then called "Tapping For Tracking"): WHICH OF THE LIFT'S TWO TAGS a
// control is managing. The customer-facing RFID sticker in the lift car, or the internal POP Tag
// sited with the paper log card in the motor room.
//
// MIND THE NAME. This is deliberately NOT called `kind`: `RfidAssetKind` above already owns that
// word on the same components and answers a completely different question (what the tag is stuck
// to, a lift or a care hoist). The two are orthogonal and both are passed together on the register's
// equipment rows, so collapsing them into one prop would silently break either the URL shape or the
// endpoint the office hits.
export type TagKind = "rfid" | "tap";

// Derive the tag status from the two stored facts. "programmed" is reserved for a future
// confirmation mechanism (nothing sets it today); the model supports it so the UI need not
// change when confirmation arrives.
//
// The parameters are named for the FACTS rather than for the RFID columns (Session AMZ renamed
// them): the attendance tap has the identical two-fact shape (a token, and when it was sent to an
// engineer), so it reuses this function rather than carrying a second copy of the same three lines
// that could drift from it.
export function rfidTagStatus(token: string | null | undefined, sentAt: string | null | undefined): RfidTagStatus {
  if (!token) return "not_programmed";
  if (sentAt) return "sent";
  return "url_generated";
}

export interface RfidStatusMeta {
  label: string;
  cls: string; // text colour class
  detail: string;
}

// Plain-English label + brand colour + one-line explanation per status.
export const RFID_STATUS_META: Record<RfidTagStatus, RfidStatusMeta> = {
  not_programmed: {
    label: "Not programmed",
    cls: "text-ele-body opacity-70",
    detail: "No RFID URL has been generated yet.",
  },
  url_generated: {
    label: "URL generated",
    cls: "text-amber-400",
    detail: "A URL has been generated. Send it to the engineer to programme the sticker on site.",
  },
  sent: {
    label: "Sent to engineer",
    cls: "text-ele-blue",
    detail: "The URL has been sent to the engineer to programme the sticker on site.",
  },
  programmed: {
    label: "Programmed",
    cls: "text-ele-green",
    detail: "The physical RFID sticker has been programmed and is in use.",
  },
};

// The public RFID service URL the physical tag resolves to, built from the app origin (the
// office page constructs it client-side; the Director re-points the physical NTAG213 tags to
// this V2 URL at cutover). Token is the bare unguessable hex string in the path. A lift resolves
// to /nfc/<token>; care EQUIPMENT (Session ACT) resolves to /nfc/e/<token> (its own one-screen
// page), keeping the same unguessable-token security -- the sub-ID is shown on the page, never in
// the URL.
export function rfidUrl(origin: string, token: string, kind: RfidAssetKind = "lift"): string {
  return kind === "equipment" ? `${origin}/nfc/e/${token}` : `${origin}/nfc/${token}`;
}

// The INTERNAL attendance tap URL the motor room sticker resolves to (Session AMZ). There is no
// equipment variant on purpose: the attendance tag has ONE page shape, because that page shows
// nothing but the lift's own reference and the time recorded, so there is nothing for a second
// asset kind to render differently. A separate /tap/ path (not a flag on /nfc/) is what keeps the
// two token spaces, and the two audiences, apart.
export function tapUrl(origin: string, token: string): string {
  return `${origin}/tap/${token}`;
}

// The tap's own status wording. Same four states and the same derivation (rfidTagStatus above),
// different nouns: this tag is programmed and fixed beside the paper log card in the motor room,
// never in the car where a resident sees it, so the copy must never say "sticker" in the sense the
// RFID card uses it.
export const TAP_STATUS_META: Record<RfidTagStatus, RfidStatusMeta> = {
  not_programmed: {
    label: "Not programmed",
    cls: "text-ele-body opacity-70",
    detail: "No POP Tag URL has been generated yet.",
  },
  url_generated: {
    label: "URL generated",
    cls: "text-amber-400",
    detail: "A URL has been generated. Send it to the engineer to programme the motor room tag.",
  },
  sent: {
    label: "Sent to engineer",
    cls: "text-ele-blue",
    detail: "The URL has been sent to the engineer to programme the motor room tag.",
  },
  programmed: {
    label: "Programmed",
    cls: "text-ele-green",
    detail: "The motor room tag has been programmed and is in use.",
  },
};

// Which status wording a control shows, chosen by the tag it manages. One lookup rather than a
// conditional at every use site, so a control that forgets to switch reads wrong in one obvious
// place instead of in four scattered ones.
export function tagStatusMeta(tagKind: TagKind): Record<RfidTagStatus, RfidStatusMeta> {
  return tagKind === "tap" ? TAP_STATUS_META : RFID_STATUS_META;
}

// The engineer-facing auto-dialler number, appended to the "send to engineer" sticker message so
// the engineer has the activation line to hand on site. This is the LIFT emergency phone
// reprogramming line (Session ACJ): it is INTERNAL, distinct from the single client-facing line
// 0203 576 4760, and it must never appear on the public RFID service page (rfidSendMessage is used
// only by the office RfidTagButton, never by the public pages). Care EQUIPMENT has no auto-dialler,
// so an equipment message OMITS this line entirely (Session ACT).
export const RFID_AUTO_DIALLER = "0203 576 4184";

// The pre-filled "send to engineer" message. Plain English, proper punctuation, no " -- "
// (house rule). The office can edit it before sending. For a LIFT the auto-dialler line is appended
// for the engineer (internal only, never customer-facing); for care EQUIPMENT it is omitted (there
// is no auto-dialler to reprogram). `kind` defaults to "lift" so existing lift callers are unchanged.
export function rfidSendMessage(opts: { assetRef: string; siteName: string; url: string; kind?: RfidAssetKind }): string {
  const site = opts.siteName.trim() || "site";
  if (opts.kind === "equipment") {
    return `RFID tag URL for ${opts.assetRef} at ${site}: ${opts.url}. Please programme the RFID sticker on the equipment with this URL.`;
  }
  return `RFID tag URL for ${opts.assetRef} at ${site}: ${opts.url}. Please programme the RFID sticker on site with this URL. Auto-dialler: ${RFID_AUTO_DIALLER}.`;
}

// The pre-filled "send to engineer" message for the INTERNAL attendance tag (Session AMZ). Its whole
// job is to stop the engineer fixing this tag where a customer RFID sticker goes, so it says in
// plain words where it belongs and what it is not.
//
// IT NEVER CARRIES THE AUTO-DIALLER LINE, and that is not an oversight to tidy up later. The
// auto-dialler is the lift EMERGENCY PHONE reprogramming number (Session ACJ): it belongs on the
// message about the car sticker because the engineer is already in the car doing that job. It has
// nothing to do with an attendance tag in the motor room, and repeating an unrelated phone number
// on every message is how a number ends up dialled by mistake.
//
// There is no `kind` here either: the attendance tag is a lift motor room fixture, so unlike
// rfidSendMessage there is no care-equipment wording to switch to.
export function tapSendMessage(opts: { assetRef: string; siteName: string; url: string }): string {
  const site = opts.siteName.trim() || "site";
  return (
    `Guv POP Tag URL for ${opts.assetRef} at ${site}: ${opts.url}. ` +
    `This is the internal POP Tag that goes with the paper log card in the motor room. ` +
    `It is not a customer RFID sticker, so please programme it and fix it beside the log card, ` +
    `not in the lift car.`
  );
}

// Normalise a mobile number for a wa.me link: digits only, no "+", in international form (the
// business is UK). Handles the everyday ways a UK mobile is written: "07...", "+44 7...",
// "447...", the international "0044 7..." and the printed trunk-prefix "+44 (0)7...". Returns
// null when there is nothing plausible, so the caller shows a warning instead of a broken link.
export function whatsAppNumber(raw: string | null | undefined): string | null {
  if (!raw) return null;
  let digits = raw.replace(/[^0-9]/g, "");
  if (!digits) return null;
  if (digits.startsWith("00")) digits = digits.slice(2); // international "00" prefix
  if (digits.startsWith("0")) digits = `44${digits.slice(1)}`; // UK trunk 0 -> country code
  if (digits.startsWith("440")) digits = `44${digits.slice(3)}`; // strip a leftover trunk 0 ("+44 (0)7...")
  // A real international number is at least ~8 digits; anything shorter is malformed input.
  return digits.length >= 8 ? digits : null;
}

// Build a wa.me deep link for an already-normalised number + a message.
export function whatsAppHref(number: string, message: string): string {
  return `https://wa.me/${number}?text=${encodeURIComponent(message)}`;
}

// Build a mailto link. The address is used verbatim (encoding the "@" upsets some mail
// clients); only the subject and body are percent-encoded.
export function mailtoHref(email: string, subject: string, body: string): string {
  return `mailto:${email.trim()}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}
